import type { TableMetric } from "@/features/analytics/constants";

import * as React from "react";

import { useCurrentWorkspaceContext } from "@editor/contexts/WorkspaceDashboardContext";

import { formatValueToTableCell } from "@/editor/utils/analytics";
import { METRICS_ALLOWING_ZERO_VALUE_WHEN_NO_DATA } from "@/features/analytics/constants";
import { useAnalyticsQueryContext } from "@/features/analytics/contexts/AnalyticsQueryContext";
import useAnalyticsStatus from "@/features/analytics/useAnalyticsStatus";
import { Tag } from "@replo/design-system/components/tag/Tag";
import twMerge from "@replo/design-system/utils/twMerge";
import { ArrowDown, ArrowUp, Minus } from "lucide-react";
import { formatWithCommasAndTwoDecimals } from "replo-utils/lib/math";

const MetricWithDelta: React.FC<{
  name: TableMetric;
  value: number | null;
  delta?: number;
  doesCompareValueExist: boolean;
  wrapperClassName?: string;
  valueClassName?: string;
  tagLayoutClassName?: string;
  touchType?: string;
  isFractional?: boolean;
}> = ({
  name,
  value,
  delta,
  doesCompareValueExist,
  wrapperClassName,
  valueClassName,
  tagLayoutClassName,
  touchType,
  isFractional,
}) => {
  const { workspace } = useCurrentWorkspaceContext();
  const { query } = useAnalyticsQueryContext();
  const { dataDisplayType } = useAnalyticsStatus();
  const getVariant = (() => {
    if (!delta || !doesCompareValueExist) {
      return "neutral";
    }
    return delta > 0 ? "success" : "danger";
  })();

  const getIcon = (() => {
    if (!doesCompareValueExist) {
      return <Minus size={12} />;
    }
    if (delta && delta !== 0) {
      return delta > 0 ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
    }
  })();

  return (
    <div
      className={twMerge(
        "justify-left inline-flex items-center gap-3 whitespace-nowrap",
        wrapperClassName,
      )}
    >
      <div className={twMerge("min-w-[10px]", valueClassName)}>
        {/* NOTE (Max, 2025-06-13): For some metrics, in the case where there's no data, we
        want to show 0 instead of a minus sign (such as conversions, revenue). However, for other
        metrics like conversion_rates or AOV, they're technically not 0 if there's no data (as they are
        rates). Instead, they're undefined, so we show minus sign. */}
        {value !== null ||
        METRICS_ALLOWING_ZERO_VALUE_WHEN_NO_DATA.includes(name) ? (
          formatValueToTableCell({
            value: value ?? 0,
            name,
            currency: workspace?.currency ?? "USD",
            touchType,
            isFractional: isFractional ?? query.isFractional,
          })
        ) : (
          <Minus size={14} />
        )}
      </div>
      {delta !== undefined && (
        <Tag
          size="sm"
          variant={getVariant}
          startEnhancer={getIcon}
          layoutClassName={twMerge(
            tagLayoutClassName,
            dataDisplayType === "partialData" && "blur-xs",
          )}
        >
          {doesCompareValueExist &&
            `${formatWithCommasAndTwoDecimals(Math.abs(delta), true)}%`}
        </Tag>
      )}
    </div>
  );
};

export default MetricWithDelta;
